import { FormControlLabel, Switch, TextField } from "@mui/material";
import { FIELD_NUMBER_MAXVALUE } from "app/appConstants";
import { MprcStringToHtml, MprcText } from "components";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcDateTimePicker } from "components/MprcDateTimePicker";
import { MprcTextFieldMask } from "components/MprcTextFieldMask";
import { MprcTextFieldNumberMask } from "components/MprcTextFieldNumberMask/MprcTextFieldNumberMask";
import { MprcTimePicker } from "components/MprcTimePicker";
import { AutoCompleteModel, CustomFieldFillConstraint, CustomFieldObjectType, CustomFieldType, CustomFieldUsages, CustomFieldValueDto, CustomFieldWidth, TagUsage, ValueSelectionFieldValue } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { validate as uuidValidate } from 'uuid';
import { GroupAutoComplete } from "views/foundation/group/components/GroupAutoComplete";
import { TagAutoComplete } from "views/foundation/tag/components/TagAutoComplete";
import { TopicAutoCompleteHierarchical } from "views/foundation/topic/components/TopicAutoCompleteHierarchical";
import { UserAutoComplete } from "views/foundation/user/components/UserAutoComplete";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    field: CustomFieldValueDto;
    error?: React.ReactNode;
    validateOnFirstMount?: boolean;
    maxLength?: number;
    useDefaultValue?: boolean;
    margin?: MuiMargin;
    disabled?: boolean;
    onChange: (value: any, formattedValue: string | undefined, isValid: boolean, errorMessage?: string) => void;
}

export function getCustomFieldGridSize(width: CustomFieldWidth): number {
    return width === CustomFieldWidth.Large ? 12
        : width === CustomFieldWidth.Medium ? 6
            : 3;
}

////////////////////////////////////////////////////////////////////////////

export const CustomFieldInput = React.forwardRef<Element, IProps>((props, ref) => {
    ////////////////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { margin, onChange, validateOnFirstMount = false, useDefaultValue = true, disabled } = props;
    const {
        fieldId,
        fieldType,
        fieldValue,
        usage,
        defaultValue,
        label,
        isRequired,
        valueSelectionField,
        textField,
        numericField,
        validationErrorMessage,
        placeholderText,
        fillConstraint,
        objectSelectionField
    } = props.field;

    const [error, setError] = useState<React.ReactNode>(undefined);
    const defaultErrorMessage = t("Messages.InvalidValueText", { ns: "shared" });


    const isReadonly = disabled || fillConstraint === CustomFieldFillConstraint.Readonly || props.field.isReadonly;

    ////////////////////////////////////////////////////////////////////////////

    ///
    const getValueSelectionSuggestions = (
        searchText: string,
        pageNumber: number,
        pageSize: number
    ): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();
        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            let items = [] as AutoCompleteDataModel[];

            if (valueSelectionField?.selectionList !== undefined) {
                items = valueSelectionField!
                    .selectionList!.filter((x) => x.key.includes(searchText))
                    .map((x) => ({ key: x.key, label: x.label, canDelete: true } as AutoCompleteDataModel));
            }
            resolve({ items, totalRows: items.length });
        });
    };

    const defaultError = validationErrorMessage ?? defaultErrorMessage;

    ///
    const validateField = (value: any): string | undefined => {
        let errorMessage = undefined;

        //Campo já foi definido
        if (value !== undefined) {

            //Campo está vazio
            if ((value ?? "").toString().length === 0) {
                if (isRequired) errorMessage = t("Messages.RequiredFieldText", { ns: "shared" });
            } else {
                switch (fieldType) {
                    case CustomFieldType.Text:
                        if (textField?.validation != undefined) {
                            var regex = new RegExp(textField.validation);
                            if (!regex.test(value)) {
                                errorMessage = defaultError;
                            }
                        }
                        break;

                    case CustomFieldType.Numeric:
                        if (Number(value) > FIELD_NUMBER_MAXVALUE) {
                            errorMessage = defaultError;
                        }
                        break;

                    case CustomFieldType.Date:
                    case CustomFieldType.DateTime:
                        if (!!value && !moment(value).isValid()) {
                            errorMessage = defaultError;
                        }
                        break;
                    case CustomFieldType.Time:
                        if (!!value && (!moment(value).isValid() && !moment(`1700-1-1 ${value}`).isValid())) {
                            errorMessage = defaultError;
                        }
                        break;

                    case CustomFieldType.Uuid:
                        if (!!value && !uuidValidate(value)) {
                            errorMessage = defaultError;
                        }
                        break;
                }
            }
        }

        return errorMessage;
    };

    ///
    const onChangeFieldValue = (value: any, formattedValue: string | undefined, invalid: boolean = false) => {
        let errorMessage = invalid ? defaultError : validateField(value);
        let isValid = errorMessage === undefined;
        setError(errorMessage);

        onChange(value, formattedValue, isValid, errorMessage);
    };

    ////////////////////////////////////////////////////////////////////////////

    //Ao rendenderizar pela primeira vez
    useEffect(() => {
        //Se não tiver valor atribuído, atribui o defaultValue (se existir)
        if ((fieldValue === undefined || fieldValue === null) && useDefaultValue && defaultValue != undefined) {
            if (fieldType == CustomFieldType.ValueSelection) {
                onChangeFieldValue(defaultValue, defaultValue);
            }
            else if (fieldType == CustomFieldType.ObjectSelection) {
                const items = JSON.parse(defaultValue) as AutoCompleteModel[];
                props.field.objectSelectionField!.selectionList = items as AutoCompleteModel[];
                onChangeFieldValue(
                    JSON.stringify(items.map(x => x.key)),
                    JSON.stringify(items.map(x => x.label))
                );
            }
            else {
                onChangeFieldValue(defaultValue, undefined);
            }
        }

        /* Se validateOnInit = true, força um inicialização com alteração de estado com o próprio fieldValue
        (para ser possível retornar no onChange se é um valor inválido ou não) */
        if (validateOnFirstMount === true && fieldValue != undefined && fieldType != CustomFieldType.ObjectSelection && fieldType != CustomFieldType.ValueSelection) {
            onChangeFieldValue(fieldValue, undefined);
        }
    }, []);

    //Ao alterar a mensagem de erro externa (props.error)
    useEffect(() => {
        if (props.error !== undefined) setError(props.error);
    }, [props.error]);

    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {fieldType === CustomFieldType.Text && (
                <>
                    {textField?.multiline === true ? (
                        <TextField
                            fullWidth
                            name={fieldId}
                            margin={margin}
                            label={label}
                            value={fieldValue ?? ""}
                            disabled={isReadonly}
                            onChange={(e) => onChangeFieldValue(e.target.value, e.target.value)}
                            required={isRequired}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: props.maxLength ?? textField?.length ?? 4096 }
                                }
                            }}
                            multiline
                            error={!!error}
                            placeholder={placeholderText ?? undefined}
                            helperText={Array.isArray(error) ? <MprcStringToHtml text={error as string[]} color="error" /> : error}
                            inputRef={ref}
                        />
                    ) : (
                        <MprcTextFieldMask
                            mask={textField?.inputMask ?? ""}
                            fullWidth
                            name={fieldId}
                            margin={margin}
                            label={label}
                            value={fieldValue ?? ""}
                            disabled={isReadonly}
                            onChange={(e) => onChangeFieldValue(e.target.value, e.target.value)}
                            required={isRequired}
                            maxLength={props.maxLength ?? textField?.length ?? 4096}
                            error={!!error}
                            placeholder={placeholderText ?? undefined}
                            helperText={Array.isArray(error) ? <MprcStringToHtml text={error as string[]} color="error" /> : error}
                            inputRef={ref}
                        />
                    )}
                </>
            )}
            {fieldType === CustomFieldType.Uuid && (
                <MprcTextFieldMask
                    mask="********-****-****-****-************"
                    fullWidth
                    name={fieldId}
                    margin={margin}
                    label={label}
                    value={(fieldValue ?? "").replace(/-/g, "")}
                    disabled={isReadonly}
                    onChange={(e) => onChangeFieldValue(e.target.value, e.target.value)}
                    required={isRequired}
                    error={!!error}
                    placeholder={placeholderText ?? undefined}
                    helperText={error}
                    inputRef={ref}
                    unmaskedValue={false}
                />
            )}
            {fieldType === CustomFieldType.Numeric && (
                <MprcTextFieldNumberMask
                    fullWidth
                    name={fieldId}
                    margin={margin}
                    label={label}
                    value={fieldValue ?? ""}
                    disabled={isReadonly}
                    onChange={(value, formattedValue) => {
                        onChangeFieldValue(
                            value,
                            formattedValue
                        );
                    }}
                    required={isRequired}
                    error={error != undefined}
                    placeholder={placeholderText ?? undefined}
                    helperText={error}
                    min={numericField?.minValue ?? undefined}
                    max={numericField?.maxValue ?? undefined}
                    precision={numericField?.precision ?? undefined}
                    mask={numericField?.inputMask ?? undefined}
                    inputRef={ref}
                    isCurrency={numericField?.isCurrency ?? false}
                    errorMessage={validationErrorMessage ?? undefined}
                />
            )}
            {fieldType === CustomFieldType.Boolean && (
                <FormControlLabel
                    name={fieldId}
                    label={label}
                    disabled={isReadonly}
                    control={
                        <Switch
                            inputRef={ref}
                            checked={LibUtils.booleanParse(fieldValue ?? "false")}
                            onChange={(e) => onChangeFieldValue(e.target.checked, e.target.checked ? "Sim" : "Não")}
                        />
                    }
                />
            )}
            {fieldType === CustomFieldType.Date && (
                <MprcDatePicker
                    label={label}
                    value={fieldValue == null ? null : moment(fieldValue).toDate()}
                    disabled={isReadonly}
                    onChange={(v, valid) => {
                        const mValue = v ? moment(v).format("YYYY-MM-DD") : v;
                        onChangeFieldValue(mValue, moment(v).format("L"), !valid);
                    }}
                    fullWidth
                    margin={margin}
                    required={isRequired}
                    error={!!error}
                    helperText={!!error && <MprcText color="error">{defaultErrorMessage}</MprcText>}
                />
            )}
            {fieldType === CustomFieldType.DateTime && (
                <MprcDateTimePicker
                    dateLabel={label}
                    value={fieldValue == null ? null : moment(fieldValue).toDate()}
                    margin={margin}
                    required={isRequired}
                    error={!!error}
                    helperText={!!error && <MprcText color="error">{defaultErrorMessage}</MprcText>}
                    disabled={isReadonly}
                    onChange={(v, valid) => onChangeFieldValue(v, v ? moment(v).format("DD/MM/YYYY HH:mm") : undefined, !valid)}
                />
            )}
            {fieldType === CustomFieldType.Time && (
                <MprcTimePicker
                    label={label}
                    value={fieldValue == null ? null : moment(fieldValue, "HH:mm:ss").toDate()}
                    margin={margin}
                    required={isRequired}
                    disabled={isReadonly}
                    error={!!error}
                    helperText={!!error && <MprcText color="error">{defaultErrorMessage}</MprcText>}
                    onChange={(v, valid) => {
                        var hour: Date | null | string = v;
                        var hourFormatted: string | undefined = undefined;

                        if (v && valid) {
                            hour = hourFormatted = moment(v).format("HH:mm:ss");
                        }

                        onChangeFieldValue(hour, hourFormatted, !valid);
                    }}
                />
            )}
            {fieldType === CustomFieldType.ValueSelection && (
                <MprcAutoComplete
                    fullWidth
                    margin={margin}
                    label={label}
                    multiSelect={valueSelectionField?.allowMultiValue ?? false}
                    placeholder={placeholderText ?? undefined}
                    disabled={isReadonly}
                    selectedItems={(JSON.parse(fieldValue ?? "[]") as string[]).map(x => ({ key: x, label: x, canDelete: true } as AutoCompleteDataModel))}
                    onGetSuggestions={getValueSelectionSuggestions}
                    onChange={(items: AutoCompleteDataModel[]) => {
                        onChangeFieldValue(
                            JSON.stringify((items as ValueSelectionFieldValue[]).map(x => x.key)),
                            JSON.stringify((items as ValueSelectionFieldValue[]).map(x => x.label)));
                    }}
                    error={error != undefined}
                    helperText={error}
                    inputRef={ref}
                    required={isRequired}
                />
            )}
            {fieldType === CustomFieldType.ObjectSelection && objectSelectionField?.objectType === CustomFieldObjectType.User &&
                (
                    <UserAutoComplete
                        label={label}
                        margin={margin}
                        multiSelect={objectSelectionField?.allowMultiValue ?? false}
                        placeholder={placeholderText ?? undefined}
                        disabled={isReadonly}
                        selectedItems={(objectSelectionField.selectionList ?? []) as AutoCompleteDataModel[]}
                        onChange={(items: AutoCompleteDataModel[]) => {
                            props.field.objectSelectionField!.selectionList = items as AutoCompleteModel[];
                            onChangeFieldValue(
                                JSON.stringify((items as AutoCompleteModel[]).map(x => x.key)),
                                JSON.stringify((items as AutoCompleteModel[]).map(x => x.label)));
                        }}
                        error={error != undefined}
                        helperText={error}
                        inputRef={ref}
                        required={isRequired}
                    />
                )
            }
            {fieldType === CustomFieldType.ObjectSelection && objectSelectionField?.objectType === CustomFieldObjectType.Tag &&
                (
                    <TagAutoComplete
                        usage={
                            usage == CustomFieldUsages.User
                                ? TagUsage.User
                                : usage == CustomFieldUsages.Unit
                                    ? TagUsage.Unit
                                    : TagUsage.Learning
                        }
                        label={label}
                        margin={margin}
                        multiSelect={objectSelectionField?.allowMultiValue ?? false}
                        placeholder={placeholderText ?? undefined}
                        disabled={isReadonly}
                        selectedItems={(objectSelectionField.selectionList ?? []) as AutoCompleteDataModel[]}
                        onChange={(items: AutoCompleteDataModel[]) => {
                            props.field.objectSelectionField!.selectionList = items as AutoCompleteModel[];
                            onChangeFieldValue(
                                JSON.stringify((items as AutoCompleteModel[]).map(x => x.key)),
                                JSON.stringify((items as AutoCompleteModel[]).map(x => x.label)));
                        }}
                        helperText={error}
                        inputRef={ref}
                        required={isRequired}
                    />
                )
            }
            {fieldType === CustomFieldType.ObjectSelection && objectSelectionField?.objectType === CustomFieldObjectType.Topic &&
                (
                    <TopicAutoCompleteHierarchical
                        label={label}
                        margin={margin}
                        multiSelect={objectSelectionField?.allowMultiValue ?? false}
                        placeholder={placeholderText ?? undefined}
                        disabled={isReadonly}
                        selectedItems={(objectSelectionField.selectionList ?? []) as AutoCompleteHierarchicalModel[]}
                        onChange={(items: AutoCompleteHierarchicalModel[]) => {
                            props.field.objectSelectionField!.selectionList = items as AutoCompleteModel[];
                            onChangeFieldValue(
                                JSON.stringify((items as AutoCompleteModel[]).map(x => x.key)),
                                JSON.stringify((items as AutoCompleteModel[]).map(x => x.label)));
                        }}
                        helperText={error}
                        inputRef={ref}
                        required={isRequired}
                        selectedMode="free"
                        onError={(e) => console.error(e)}
                    />
                )
            }
            {fieldType === CustomFieldType.ObjectSelection && objectSelectionField?.objectType === CustomFieldObjectType.Group &&
                (
                    <GroupAutoComplete
                        label={label}
                        margin={margin}
                        multiSelect={objectSelectionField?.allowMultiValue ?? false}
                        placeholder={placeholderText ?? undefined}
                        disabled={isReadonly}
                        selectedItems={(objectSelectionField.selectionList ?? []) as AutoCompleteDataModel[]}
                        onChange={(items: AutoCompleteDataModel[]) => {
                            props.field.objectSelectionField!.selectionList = items as AutoCompleteModel[];
                            onChangeFieldValue(
                                JSON.stringify((items as AutoCompleteModel[]).map(x => x.key)),
                                JSON.stringify((items as AutoCompleteModel[]).map(x => x.label)));
                        }}
                        error={error != undefined}
                        helperText={error}
                        inputRef={ref}
                        required={isRequired}
                    />
                )
            }
        </>
    );
});
